import { useAuthStore, type AuthOrg, type AuthUser } from './store';

/**
 * OIDC / PKCE client for auth.soliddigital.com (client_id "financials",
 * public client + PKCE S256) and the AUTH_MODE=dev helpers.
 *
 * Quirks (per BRIEF, from IdP recon):
 * - authorize needs the extra param `offline_access=true` (in addition to the
 *   `offline_access` scope) or no refresh token is issued.
 * - refresh tokens rotate — always keep the newest one.
 */

/**
 * Config-driven so the client_id registered in accounts.soliddigital.com →
 * /admin/applications drops straight in (`VITE_FINANCIALS_CLIENT_ID`).
 * Must match the backend's `FINANCIALS_CLIENT_ID`.
 */
export const CLIENT_ID =
  (import.meta.env.VITE_FINANCIALS_CLIENT_ID as string | undefined)?.trim() || 'financials';
/** Registered scopes — keep in sync with README → "Register the app in Accounts". */
const SCOPE = 'openid profile email system_role orgs teams apps offline_access';
const PKCE_STORAGE_KEY = 'solid-auth:pkce';

export function isDevMode(): boolean {
  return import.meta.env.VITE_AUTH_MODE === 'dev';
}

function authUrl(): string {
  const url = import.meta.env.VITE_AUTH_URL as string | undefined;
  if (!url) throw new Error('VITE_AUTH_URL is not set (required when VITE_AUTH_MODE=oidc).');
  return url.replace(/\/+$/, '');
}

function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/* ---------- PKCE helpers ---------- */

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

async function s256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/* ---------- Claims → AuthUser ---------- */

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function userFromClaims(claims: Record<string, unknown>): AuthUser {
  const email = typeof claims.email === 'string' ? claims.email : '';
  const appsRaw = Array.isArray(claims.apps) ? claims.apps : [];
  const orgsRaw = Array.isArray(claims.orgs) ? claims.orgs : [];
  return {
    sub: typeof claims.sub === 'string' ? claims.sub : undefined,
    email,
    name: typeof claims.name === 'string' ? claims.name : email,
    apps: appsRaw
      .map((app) => {
        if (typeof app === 'string') return { clientId: app };
        const record = (app ?? {}) as Record<string, unknown>;
        const clientId = record.clientId ?? record.client_id;
        // ⚠ SPREAD, do not narrow. The rail renders `user.apps` from the
        // shared solid-auth store and needs auth's own fields (client_id,
        // name, url, iconName, color). Narrowing to {clientId} here is what
        // left the rail with zero apps on financials.
        return { ...record, clientId: typeof clientId === 'string' ? clientId : '' };
      })
      .filter((app) => app.clientId !== ''),
    orgs: orgsRaw
      .map((org): AuthOrg => {
        const record = (org ?? {}) as Record<string, unknown>;
        // ⚠ THE AUTH SERVER EMITS `org_id`, NOT `id`.
        // `oidc.config.js` builds the claim as
        // `{ org_id, name, slug, logo, role, kind, isAgency }`. Reading only
        // `id` left every org with an empty id, and the `.filter()` below then
        // dropped ALL of them — so `user.orgs` was `[]`, `activeOrgId` was
        // null, and `useActiveOrgId()` returned undefined.
        //
        // That is not cosmetic. `X-Organization` is never sent (lib/api.ts
        // reads the same selector), and every query gated on `enabled:
        // Boolean(orgId)` never fires at all: the whole cash report behind
        // /dashboard, recurring invoices, /time and the timesheet reminders.
        // The symptom is a screen of skeletons that never resolve, with no
        // failed request to find — because the request is never made.
        // `lib/normalizeClaims` on the backend accepts both spellings, which
        // is why the API worked while the browser did not.
        const id =
          typeof record.id === 'string' && record.id !== ''
            ? record.id
            : typeof record.org_id === 'string'
              ? record.org_id
              : '';
        return {
          id,
          name: typeof record.name === 'string' ? record.name : '',
          slug: typeof record.slug === 'string' ? record.slug : '',
          role: typeof record.role === 'string' ? record.role : 'member',
        };
      })
      .filter((org) => org.id !== ''),
  };
}

/* ---------- OAuth flows ---------- */

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
}

async function tokenRequest(body: URLSearchParams): Promise<TokenResponse> {
  const response = await fetch(`${authUrl()}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    const description = payload.error_description ?? payload.error ?? `HTTP ${response.status}`;
    throw new Error(`Token request failed: ${String(description)}`);
  }
  if (typeof payload.access_token !== 'string') {
    throw new Error('Token response did not include an access_token.');
  }
  return payload as unknown as TokenResponse;
}

/** Redirects the browser to the IdP authorize endpoint (PKCE S256). */
export async function beginLogin(returnTo = '/dashboard'): Promise<void> {
  const verifier = randomString(48);
  const state = randomString(16);
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify({ verifier, state, returnTo }));
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: SCOPE,
    state,
    code_challenge: await s256Challenge(verifier),
    code_challenge_method: 'S256',
    // IdP quirk: refresh tokens are only issued with this extra param.
    offline_access: 'true',
  });
  window.location.assign(`${authUrl()}/oauth/authorize?${params.toString()}`);
}

export interface LoginResult {
  accessToken: string;
  refreshToken: string | null;
  user: AuthUser;
  returnTo: string;
}

/** /auth/callback: exchange the code for tokens and build the user from claims. */
export async function completeLogin(code: string, state: string): Promise<LoginResult> {
  const raw = sessionStorage.getItem(PKCE_STORAGE_KEY);
  sessionStorage.removeItem(PKCE_STORAGE_KEY);
  if (!raw) throw new Error('Sign-in session expired — please start again.');
  const saved = JSON.parse(raw) as { verifier: string; state: string; returnTo?: string };
  if (state !== saved.state) throw new Error('State mismatch — please start the sign-in again.');

  const tokens = await tokenRequest(
    new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri(),
      client_id: CLIENT_ID,
      code_verifier: saved.verifier,
    }),
  );

  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? null,
    user: await resolveUserFromToken(tokens.access_token),
    returnTo: saved.returnTo || '/dashboard',
  };
}

/**
 * Build the user from an access token — the ONE place claims are read.
 *
 * The token may be a JWT carrying sub/email/name/apps/orgs, or it may carry
 * almost nothing: this IdP's `claims()` builder populates the ID token and
 * `/oauth/userinfo`, not the access token, so userinfo is the fallback rather
 * than an optimisation. (The backend learned the same lesson separately —
 * `lib/userinfo.ts`.)
 *
 * ⚠ Exported because the LOGIN CALLBACK IS NOT THE ONLY MOMENT THIS MATTERS.
 * The store persists `user` under `solid-auth`, so a returning session replays
 * whatever shape was saved the day it signed in. When the org claim's key
 * changed spelling, every existing session kept an empty `orgs` array — and
 * every query gated on `enabled: Boolean(orgId)` silently never fired, which
 * is a dashboard of skeletons that never resolve. Re-deriving at boot is what
 * makes a claims fix reach people who are already signed in.
 */
export async function resolveUserFromToken(accessToken: string): Promise<AuthUser> {
  let claims = decodeJwtPayload(accessToken) ?? {};
  if (!claims.email || !Array.isArray(claims.orgs)) {
    try {
      const response = await fetch(`${authUrl()}/oauth/userinfo`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) claims = { ...claims, ...((await response.json()) as Record<string, unknown>) };
    } catch {
      /* claims from the JWT will have to do */
    }
  }
  return userFromClaims(claims);
}

/** Silent refresh. Returns rotated tokens (newest refresh token wins). */
export async function refreshTokens(
  refreshToken: string,
): Promise<{ accessToken: string; refreshToken: string }> {
  const tokens = await tokenRequest(
    new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: CLIENT_ID,
    }),
  );
  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? refreshToken,
  };
}

/**
 * Sign out: clear the local store; in OIDC mode also end the IdP session via
 * the logout endpoint. In dev mode the router redirects to /login on its own.
 */
export function signOut(): void {
  const oidc = !isDevMode();
  useAuthStore.getState().clear();
  if (oidc) {
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      post_logout_redirect_uri: `${window.location.origin}/login`,
    });
    window.location.assign(`${authUrl()}/oauth/logout?${params.toString()}`);
  }
}
